<script setup>
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'
const { y } = useWindowScroll()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

const story = await useAsyncStoryblok('components/layout/header', { version: 'publish', language: locale.value })

if (route.query._storyblok && route.query['_storyblok_tk[token]']) {
  const draft = await $fetch('/api/storyblok/preview', {
    query: {
      slug: 'components/layout/header',
      language: locale.value,
      space_id: route.query['_storyblok_tk[space_id]'],
      timestamp: route.query['_storyblok_tk[timestamp]'],
      token: route.query['_storyblok_tk[token]'],
    },
  }).catch(() => null)
  if (draft?.story)
    story.value = draft.story
}

const navbarMobile = ref(null)
const navbarDesktop = ref(null)
const navbarSize = ref(0)
const activeIndexLv1 = ref(null)
const activeIndexLv2 = ref(0)
const activeSideMenu = ref(false)
const affixActive = ref(true)
const { width } = useWindowSize()

function openMenuLevel2(menu, index) {
  menu.active = !menu.active
  activeIndexLv2.value = index
}

function openSideMenu() {
  activeSideMenu.value = true
  document.body.style.overflow = 'hidden'
}
function closeSideMenu() {
  activeSideMenu.value = false
}

function affixHeader() {
  const element = navbarSize.value
  if (y.value < element)
    affixActive.value = true

  else
    affixActive.value = false
}

function onWindowResize() {
  const navbMobile = document.querySelectorAll('header.header-sm')
  const navDesktop = document.querySelectorAll('header.header-lg')

  if (width.value < 1024) {
    navbarMobile.value = true
    navbarDesktop.value = false
    for (let i = 0; i < navbMobile.length; i++)
      navbarSize.value = navbMobile[i].clientHeight
  }
  else {
    navbarDesktop.value = true
    navbarMobile.value = false
    for (let i = 0; i < navDesktop.length; i++)
      navbarSize.value = navDesktop[i].clientHeight
  }
}

function handleClick() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function openNewTab(url) {
  window.open(url, '_blank')
}

function isMegaMenu(item, index) {
  return index === 0 || index === 3 || item.link?.includes('pipeline-services') || hasSidebar(item)
}

function isExternalLink(link) {
  return /^https?:\/\//i.test(link || '')
}

function resolveSubmenuLink(submenu) {
  if (isExternalLink(submenu.link))
    return submenu.link

  return localePath({
    path: submenu.link ? withoutTrailingSlash(submenu.link) : '',
    query: (['/contact-us', '/th/contact-us'].includes(route.path) && submenu.query) ? { value: submenu.label } : {},
  })
}

function resolveMenuLink(entry) {
  if (entry.target || isExternalLink(entry.link))
    return entry.link

  return localePath({ path: withoutTrailingSlash(entry.link || '') })
}

const NuxtLinkComponent = resolveComponent('NuxtLink')

function toMenuEntry(entry, resolve) {
  return {
    key: entry._uid,
    label: entry.label,
    to: entry.link ? resolve(entry) : undefined,
    external: !!entry.target || isExternalLink(entry.link),
  }
}

function panelGroups(menu) {
  const groups = []
  for (const child of menu.submenu || []) {
    if (child.submenu?.length) {
      groups.push({
        key: child._uid,
        heading: child.label ? toMenuEntry(child, resolveMenuLink) : null,
        items: child.submenu.filter(entry => entry.label).map(entry => toMenuEntry(entry, resolveSubmenuLink)),
      })
      continue
    }

    if (!child.label)
      continue

    const last = groups.at(-1)
    if (last && !last.heading)
      last.items.push(toMenuEntry(child, resolveMenuLink))
    else
      groups.push({ key: child._uid, heading: null, items: [toMenuEntry(child, resolveMenuLink)] })
  }
  return groups
}

function isFlatPanel(menu) {
  return !menu.submenu?.some(child => child.label && child.submenu?.length)
}

function richTextNodeHasContent(node) {
  if (!node)
    return false

  if (node.type === 'text')
    return !!node.text?.trim()

  if (Array.isArray(node.content))
    return node.content.some(richTextNodeHasContent)

  return ['image', 'blok', 'table', 'horizontal_rule'].includes(node.type)
}

function hasRichTextContent(doc) {
  return !!doc?.content?.some(richTextNodeHasContent)
}

function hasSidebar(item) {
  return hasRichTextContent(item.description)
}

function closeAllMenus() {
  activeIndexLv1.value = null
  activeIndexLv2.value = 0
  activeSideMenu.value = false
  document.body.style.overflow = ''
}

watch(() => route.path, () => {
  closeAllMenus()
})

watch(activeIndexLv1, () => {
  activeIndexLv2.value = 0
})

onMounted(() => {
  window.addEventListener('resize', onWindowResize)
  window.addEventListener('scroll', affixHeader)
  window.addEventListener('resize', affixHeader)
  onWindowResize()
  affixHeader()
})
</script>

<template>
  <div class="fixed z-200 w-full transition-transform duration-200" :class="!affixActive ? 'lg:translate-y-[-53px] affix' : 'lg:translate-y-0'">
    <LayoutsCtaHeader />
    <header
      :class="!affixActive ? 'affix-pattern affix' : ''"
      class="header-lg inset-x-0 top-0 z-200 items-center justify-center bg-white shadow-custom-lg shadow-shadow-light/10 lg:flex"
    >
      <div class="container">
        <div class="h-full flex items-center gap-x-3 py-3 lg:gap-x-0 lg:py-0">
          <!-- logo -->
          <div class="w-full justify-start lg:w-unset lg:flex lg:justify-start lg:pr-2">
            <NuxtLink aria-label="Logo" :to="localePath('/')" class="cursor-pointer" @click="handleClick">
              <div v-html="story.content.logo" />
            </NuxtLink>
          </div>

          <!-- Desktop Menu -->
          <div class="desktop hidden h-full w-full flex-1 items-center text-primary lg:flex xl:justify-end lg:justify-between 3xl:gap-x-5 lg:gap-x-2">
            <div
              v-for="(item, index) in story.content.menu" :key="item._uid"
              class="menu-item h-full flex items-center border-b-2 border-white transition-all duration-200 ease-in-out 3xl:text-sm lg:text-xs"
              :class="[locale === 'en' && route.path === item.link || locale === 'th' && `/${locale}${item.link}` === `${route.path}` ? '!border-secondary-red-100 !text-secondary-red-100' : ' hover:!border-secondary-red-100 hover:!text-secondary-red-100', !isMegaMenu(item, index) ? 'relative' : '']"
              @mouseover="activeIndexLv1 = index"
              @mouseleave="activeIndexLv1 = null"
            >
              <a
                v-if="item.target"
                class="navigation h-full flex items-center" :href="item.link" target="_blank" rel="noopener noreferrer"
              >
                <span>{{ item.label }}</span>
                <svg class="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256"><path fill="currentColor" d="M126 136v64a6 6 0 0 1-12 0v-49.51l-69.76 69.75a6 6 0 0 1-8.48-8.48L105.51 142H56a6 6 0 0 1 0-12h64a6 6 0 0 1 6 6Zm82-102H80a14 14 0 0 0-14 14v48a6 6 0 0 0 12 0V48a2 2 0 0 1 2-2h128a2 2 0 0 1 2 2v128a2 2 0 0 1-2 2h-48a6 6 0 0 0 0 12h48a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14Z" /></svg>
              </a>
              <NuxtLink
                v-else :to="localePath({ path: item.link ? withoutTrailingSlash(item.link) : '' })" class="navigation relative h-full flex items-center"
                :aria-label="`${item.label} - Dexon Technology`"
                @click="closeAllMenus"
              >
                <span class="text-[12px] 3xl:text-sm">{{ item.label }}<span class="sr-only"> - Dexon Technology</span></span>
                <div v-if="item.submenu?.length > 0" class="w-[20px]">
                  <svg class="ml-1 transition-transform group-hover/item:rotate-180" width="20" height="20" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m7 10l5 5l5-5" /></svg>
                </div>
              </NuxtLink>

              <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-150 ease-in" leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-if="item.submenu?.length > 0 && activeIndexLv1 === index"
                  class="absolute top-18 z-50 w-screen px-4 sm:px-0"
                  :class="!isMegaMenu(item, index) ? [item.width === '5xl' ? 'max-w-5xl' : '' || item.width === '4xl' ? 'max-w-4xl' : ''
                    || item.width === '3xl' ? 'max-w-3xl' : '' || item.width === '2xl' ? 'max-w-2xl' : ''
                      || item.width === 'xl' ? 'max-w-xl' : '' || item.width === 'lg' ? 'max-w-lg' : ''
                        || item.width === 'md' ? 'max-w-md' : '' || item.width === 'sm' ? 'max-w-sm' : 'max-w-xs', item.align === 'center' ? '3xl:left-1/2 3xl:-translate-x-1/2 lg:left-[-87%] left-[-100%]' : 'lg:left-1/2 lg:-translate-x-1/2'] : 'left-0 !top-[113px] !w-full'"
                >
                  <svg v-if="!isMegaMenu(item, index)" class="absolute inset-x-0 top-[-17px] mx-auto text-white" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M8.71 12.29L11.3 9.7a.996.996 0 0 1 1.41 0l2.59 2.59c.63.63.18 1.71-.71 1.71H9.41c-.89 0-1.33-1.08-.7-1.71" /></svg>
                  <div
                    class="overflow-y-auto relative grid max-h-2xl cursor-default border border-secondary-gray-200 bg-white p-5 shadow-custom-lg active:!border focus:!border"
                    :class="!isMegaMenu(item, index) ? 'rounded-2' : 'rounded-bl-2 rounded-br-2'"
                  >
                    <div
                      v-if="isMegaMenu(item, index)"
                      class="absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r from-primary via-secondary-red-100 to-primary"
                    />
                    <div :class="hasSidebar(item) ? 'grid items-start grid-cols-4 container !h-full gap-8' : isMegaMenu(item, index) ? 'grid items-start grid-cols-3 container !h-full gap-8' : ''">
                      <div v-if="hasSidebar(item)">
                        <div class="[&_p:has(b)]:!mb-3 [&_p:not(:last-child,p:has(b))]:!mb-4 mb-6">
                          <RichTextRenderer :document="item.description" />
                        </div>
                        <NuxtLink :to="resolveMenuLink(item)" class="text-sm text-secondary-red-100 font-semibold flex items-center gap-x-2" @click="closeAllMenus">
                          <span>{{ $t('button.learn') }}</span>
                          <div>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.95776 0.801575H9.00003V6.84384" stroke="#EE2334" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M1 8.80158L9 0.801587" stroke="#EE2334" stroke-width="1.25" stroke-linecap="round"/>
                              </svg>
                          </div>
                        </NuxtLink>
                      </div>
                      <div
                        :class="isMegaMenu(item, index) ? 'grid content-start gap-y-3' : item.column === 'col1' ? 'lg:grid-cols-1' : '' || item.column === 'col2' ? 'lg:grid-cols-2' : '' || item.column === 'col3' ? 'lg:grid-cols-3' : '' || item.column === 'col4' ? 'lg:grid-cols-4' : 'main-menu grid gap-x-8 gap-y-4'"
                      >
                        <div
                          v-for="(submenu_lv1, index_lv2) in item.submenu" :key="submenu_lv1._uid"
                          :class="isMegaMenu(item, index) ? submenu_lv1.divider ? '!border-b border-secondary-gray-300 border-d pb-4 mb-1' : '' : 'relative flex flex-col whitespace-normal visited:text-white focus:outline-none'">
                          <button
                            v-if="isMegaMenu(item, index) && submenu_lv1.label"
                            type="button"
                            class="group/main w-full flex items-center justify-between gap-x-3 rounded-2 !border p-4 text-left text-sm leading-snug transition-colors duration-200 ease-out focus-visible:ring-2 focus-visible:ring-secondary-red-100/40"
                            :class="activeIndexLv2 === index_lv2 && !submenu_lv1.target ? 'bg-primary font-medium text-white border-primary' : 'text-primary border-primary/15 hover:bg-primary/2 hover:border-primary/40'"
                            :aria-expanded="submenu_lv1.target ? undefined : activeIndexLv2 === index_lv2"
                            @click="submenu_lv1.target ? openNewTab(submenu_lv1.link) : openMenuLevel2(submenu_lv1, index_lv2)"
                          >
                            <span>{{ submenu_lv1.label }}</span>
                            <svg v-if="submenu_lv1.target" class="h-3.5 w-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                              <path d="M2.95776 0.801575H9.00003V6.84384" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M1 8.80158L9 0.801587" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" />
                            </svg>
                            <svg v-else-if="submenu_lv1.submenu?.length" class="h-3.5 w-3.5 shrink-0 transition-transform duration-200 group-hover/main:translate-x-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M15.187 12L7.47 4.285q-.221-.221-.218-.532q.003-.31.224-.532Q7.698 3 8.009 3q.31 0 .532.221l7.636 7.643q.242.242.354.54t.111.596t-.111.596t-.354.54L8.535 20.78q-.222.221-.53.218q-.307-.003-.528-.224t-.221-.532t.221-.531z" /></svg>
                          </button>
                          <NuxtLink
                            v-else-if="!isMegaMenu(item, index) && submenu_lv1.label"
                            :to="submenu_lv1.target ? submenu_lv1.link : localePath({ path: withoutTrailingSlash(submenu_lv1.link || '') })"
                            :target="submenu_lv1.target ? '_blank' : ''"
                            class="group/main mb-3 flex scale-[100%] items-center"
                            @click="closeAllMenus"
                          >
                            <div class="flex items-center text-sm text-primary font-bold tracking-tight group-hover/main:text-secondary-red-100">
                              <span>{{ submenu_lv1.label }}</span>
                              <div v-if="submenu_lv1.target">
                                <svg class="ml-1 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256"><path fill="currentColor" d="M126 136v64a6 6 0 0 1-12 0v-49.51l-69.76 69.75a6 6 0 0 1-8.48-8.48L105.51 142H56a6 6 0 0 1 0-12h64a6 6 0 0 1 6 6Zm82-102H80a14 14 0 0 0-14 14v48a6 6 0 0 0 12 0V48a2 2 0 0 1 2-2h128a2 2 0 0 1 2 2v128a2 2 0 0 1-2 2h-48a6 6 0 0 0 0 12h48a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14Z" /></svg>
                              </div>
                            </div>
                          </NuxtLink>
                          <div v-if="!isMegaMenu(item, index)" class="submenu-inner flex flex-col gap-x-6">
                            <div
                              v-for="(submenu_lv2, index_lv2) in submenu_lv1.submenu" :key="submenu_lv2._uid"
                              class="group/submenu group/arrow mb-2.5 text-sm text-primary/80 last:mb-0"
                            >
                              <div
                                class="submenu--list flex justify-between"
                                @click="submenu_lv2.submenu?.length ? submenu_lv2.active = !submenu_lv2.active : false"
                              >
                                <div v-if="submenu_lv2.link" :class="[submenu_lv2.submenu?.length ? 'w-[75%]' : 'w-full', { '!cursor-default': !submenu_lv2.link }]">
                                  <NuxtLink
                                    class="flex items-center justify-start font-medium hover:text-secondary-red-100"
                                    :to="resolveMenuLink(submenu_lv2)"
                                    :target="(submenu_lv2.target || isExternalLink(submenu_lv2.link)) ? '_blank' : undefined"
                                    :rel="(submenu_lv2.target || isExternalLink(submenu_lv2.link)) ? 'noopener noreferrer' : undefined"
                                    @click="closeAllMenus"
                                  >
                                    <span>{{ submenu_lv2.label }}</span>
                                    <div v-if="submenu_lv1.target">
                                      <svg class="ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256"><path fill="currentColor" d="M126 136v64a6 6 0 0 1-12 0v-49.51l-69.76 69.75a6 6 0 0 1-8.48-8.48L105.51 142H56a6 6 0 0 1 0-12h64a6 6 0 0 1 6 6Zm82-102H80a14 14 0 0 0-14 14v48a6 6 0 0 0 12 0V48a2 2 0 0 1 2-2h128a2 2 0 0 1 2 2v128a2 2 0 0 1-2 2h-48a6 6 0 0 0 0 12h48a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14Z" /></svg>
                                    </div>
                                  </NuxtLink>
                                </div>
                                <div
                                  v-else
                                  class="flex items-start justify-start font-medium hover:text-secondary-red-100"
                                  :class="[submenu_lv2.submenu?.length ? 'w-[75%]' : 'w-full', { '!cursor-default': !submenu_lv2.link }]"
                                >
                                  {{ submenu_lv2.label }}
                                </div>
                                <div
                                  v-if="submenu_lv2.submenu?.length"
                                  class="w-1/2 flex justify-end pl-6 text-[10px] text-primary/80 hover:!text-secondary-red-100"
                                >
                                  <svg
                                    class="transition-all duration-200 ease-in-out"
                                    :class="submenu_lv2.active ? '-rotate-180' : 'not-active rotate-0 '"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20" height="20" viewBox="0 0 24 24"
                                  >
                                    <path
                                      fill="currentColor"
                                      d="M8.12 9.29L12 13.17l3.88-3.88a.996.996 0 1 1 1.41 1.41l-4.59 4.59a.996.996 0 0 1-1.41 0L6.7 10.7a.996.996 0 0 1 0-1.41c.39-.38 1.03-.39 1.42 0z"
                                    />
                                  </svg>
                                </div>
                              </div>

                              <div
                                v-if="submenu_lv2.active && submenu_lv2.submenu?.length"
                                class="submenu--list-submenu relative overflow-hidden"
                              >
                                <NuxtLink
                                  v-for="submenu_lv3 in submenu_lv2.submenu" :key="submenu_lv3._uid"
                                  :to="resolveSubmenuLink(submenu_lv3)"
                                  :target="isExternalLink(submenu_lv3.link) ? '_blank' : undefined"
                                  :rel="isExternalLink(submenu_lv3.link) ? 'noopener noreferrer' : undefined"
                                  :class="submenu_lv3.query ? '[&>*]:!text-primary [&>*]:hover:!text-secondary-red-100' : ''"
                                  @click="closeAllMenus"
                                >
                                  <div class="flex py-1.5 hover:text-secondary-red-100">
                                    <span class="ml-3 flex items-center font-medium">
                                      <svg
                                        class="mr-1.5" xmlns="http://www.w3.org/2000/svg" width="7" height="7"
                                        viewBox="0 0 16 16"
                                      >
                                        <path fill="currentColor" d="M8 4a4 4 0 1 1 0 8a4 4 0 0 1 0-8Z" />
                                      </svg>
                                      {{ submenu_lv3.label }}
                                    </span>
                                  </div>
                                </NuxtLink>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="isMegaMenu(item, index)" class="col-span-2 min-w-0">
                        <div
                          v-for="submenu_lv1 in item.submenu.filter((_, panelIndex) => panelIndex === activeIndexLv2)"
                          :key="submenu_lv1._uid"
                          class="mega-panel"
                        >
                          <NuxtLink
                            v-if="submenu_lv1.label && submenu_lv1.link && !submenu_lv1.target"
                            :to="resolveMenuLink(submenu_lv1)"
                            :target="isExternalLink(submenu_lv1.link) ? '_blank' : undefined"
                            :rel="isExternalLink(submenu_lv1.link) ? 'noopener noreferrer' : undefined"
                            class="group/title relative mb-4 flex items-center justify-between gap-x-6 overflow-hidden rounded-r-2 bg-primary/3 py-3 pl-5 pr-4 focus-visible:ring-2 focus-visible:ring-secondary-red-100/40"
                            @click="closeAllMenus"
                          >
                            <span class="absolute inset-y-0 left-0 w-1 bg-secondary-red-100" aria-hidden="true" />
                            <p class="min-w-0 truncate text-base text-primary font-semibold leading-snug xl:text-lg">
                              {{ submenu_lv1.label }}
                            </p>
                            <span class="inline-flex shrink-0 items-center gap-x-1.5 text-sm text-secondary-red-100 font-semibold">
                              <span class="underline-offset-4 group-hover/title:underline">{{ $t('button.learn') }}</span>
                              <svg class="transition-transform duration-300 group-hover/title:translate-x-0.5 group-hover/title:-translate-y-0.5" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M2.95776 0.801575H9.00003V6.84384" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M1 8.80158L9 0.801587" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" />
                              </svg>
                            </span>
                          </NuxtLink>

                          <div class="max-h-[min(34rem,calc(100vh-19rem))] overflow-y-auto overscroll-contain pr-1">
                            <div :class="isFlatPanel(submenu_lv1) ? '' : hasSidebar(item) ? 'xl:columns-2 gap-x-8' : 'columns-2 gap-x-8'">
                              <div
                                v-for="group in panelGroups(submenu_lv1)" :key="group.key"
                                class="mb-6 break-inside-avoid last:mb-0"
                              >
                                <div v-if="group.heading" class="mb-1 px-3">
                                  <component
                                    :is="group.heading.to ? NuxtLinkComponent : 'p'"
                                    :to="group.heading.to"
                                    :target="group.heading.external ? '_blank' : undefined"
                                    :rel="group.heading.external ? 'noopener noreferrer' : undefined"
                                    class="inline-flex items-center gap-x-1.5 text-sm text-primary font-semibold leading-snug"
                                    :class="group.heading.to ? 'rounded-sm transition-colors duration-200 hover:text-secondary-red-100 focus-visible:ring-2 focus-visible:ring-secondary-red-100/40' : ''"
                                    @click="group.heading.to && closeAllMenus()"
                                  >
                                    {{ group.heading.label }}
                                  </component>
                                </div>
                                <ul
                                  class="m-0 grid list-none gap-y-0.5 p-0"
                                  :class="isFlatPanel(submenu_lv1) ? hasSidebar(item) ? 'xl:grid-cols-2 gap-x-6' : 'grid-cols-2 gap-x-6' : ''"
                                >
                                  <li v-for="entry in group.items" :key="entry.key" class="m-0">
                                    <component
                                      :is="entry.to ? NuxtLinkComponent : 'span'"
                                      :to="entry.to"
                                      :target="entry.external ? '_blank' : undefined"
                                      :rel="entry.external ? 'noopener noreferrer' : undefined"
                                      class="group/row relative flex items-start gap-x-2 rounded-lg px-3 py-2.5 text-sm text-primary/80 leading-snug"
                                      :class="entry.to ? 'transition-colors duration-200 hover:bg-primary/4 hover:text-primary focus-visible:ring-2 focus-visible:ring-secondary-red-100/40 [&.router-link-exact-active]:bg-secondary-red-100/5' : 'cursor-default'"
                                      @click="entry.to && closeAllMenus()"
                                    >
                                      <span class="h-[1.375em] w-3.5 flex shrink-0 items-center" aria-hidden="true">
                                        <span class="h-1.5 w-1.5 rounded-full bg-secondary-red-100 transition-all duration-200 ease-out group-hover/row:h-[3px] group-hover/row:w-3" />
                                      </span>
                                      <span class="min-w-0 flex-1">{{ entry.label }}</span>
                                      <svg v-if="entry.to && entry.external" class="mt-[0.33em] h-2.5 w-2.5 shrink-0 text-primary/40 transition-colors group-hover/row:text-secondary-red-100" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M2.95776 0.801575H9.00003V6.84384" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M1 8.80158L9 0.801587" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" />
                                      </svg>
                                      <svg v-else-if="entry.to" class="mt-[0.1875em] h-3.5 w-3.5 shrink-0 text-secondary-red-100 opacity-0 transition-all duration-200 ease-out -translate-x-1 group-hover/row:translate-x-0 group-hover/row:opacity-100" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.64l-3.96-3.96a.75.75 0 1 1 1.06-1.06l5.25 5.25a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 1 1-1.06-1.06l3.96-3.96H3.75A.75.75 0 0 1 3 10Z" clip-rule="evenodd" />
                                      </svg>
                                    </component>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-for="submenu_lv1 in item.submenu" :key="submenu_lv1._uid" class="shadow-custom-lg -translate-y-1.5">
                    <BannerCaseStudie
                      v-if="submenu_lv1.component === 'banner'"
                      :blok="submenu_lv1.actions"
                      :content="submenu_lv1.title"
                    />
                  </div>
                </div>
              </transition>
            </div>
            <UIChangeLangeuage class="menu-item relative flex cursor-pointer items-center" />
          </div>

          <!-- hamburger Menu -->
          <div v-if="navbarMobile" class="w-full flex items-center justify-end gap-x-4 lg:order-2 lg:hidden">
            <div class="flex items-center gap-x-3">
              <UISearchBar class="flex lg:hidden" />
              <UIChangeLangeuage class="relative flex cursor-pointer items-center lg:hidden" />
            </div>
            <button
              data-collapse-toggle="navbar-sticky"
              type="button"
              class="inline-flex items-center text-sm text-white lg:hidden"
              aria-controls="navbar-sticky"
              aria-expanded="false"
              @click="openSideMenu()"
            >
              <span class="sr-only">Open main menu</span>
              <Hamburger />
            </button>
          </div>
          <!-- Mobile Menu -->
          <div v-if="navbarMobile">
            <transition
              enter-active-class="transition-opacity"
              enter-from-class="opacity opacity-0"
              enter-to-class="opacity opacity-100"
              leave-active-class="transition-opacity"
              leave-from-class="opacity opacity-100"
              leave-to-class="opacity opacity-0"
            >
              <div v-if="activeSideMenu" class="fixed inset-0 z-[100] flex items-start justify-start overflow-y-auto lg:hidden">
                <LayoutsMobileMenu
                  class="z-50 h-full w-full xs:w-auto"
                  :blok="story.content.menu"
                  :actions="story.content.actions"
                  @close-side-menu="closeSideMenu"
                />
              </div>
            </transition>
          </div>
        </div>
      </div>
    </header>
    <LayoutsCrawlableMenu :menu="story.content.menu" />
  </div>
</template>

<style scoped>
.mega-panel {
  animation: mega-panel-in 0.25s ease-out;
}

@keyframes mega-panel-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mega-panel {
    animation: none;
  }
}
</style>
